import { auth, logAnalyticsEvent } from '@/firebase/config'
import { 
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  signInAnonymously,
  getIdTokenResult
} from 'firebase/auth'

export default {
  namespaced: true,

  state: {
    user: null,
    isAuthenticated: false,
    isAdmin: false,
    loading: false,
    error: null,
    initialized: false
  },

  mutations: {
    SET_USER(state, user) {
      state.user = user
    },
    SET_LOADING(state, status) {
      state.loading = status
    },
    SET_ERROR(state, error) {
      state.error = error
    },
    SET_INITIALIZED(state, status) {
      state.initialized = status
    },
    SET_IS_AUTHENTICATED(state, status) {
      state.isAuthenticated = status
    },
    SET_IS_ADMIN(state, status) {
      state.isAdmin = status
    }
  },

  actions: {
    init({ commit }) {
      return new Promise((resolve) => {
        // Set up auth state listener
        onAuthStateChanged(auth, async (user) => {
          if (user) {
            // Check admin based on email address as defined in Firestore rules
            const isAdmin = user.email === 'thehollowaynorwich@gmail.com';
            
            commit('SET_USER', {
              email: user.email,
              uid: user.uid,
              isAnonymous: user.isAnonymous
            })
            commit('SET_IS_AUTHENTICATED', true)
            commit('SET_IS_ADMIN', isAdmin)
          } else {
            commit('SET_USER', null)
            commit('SET_IS_AUTHENTICATED', false)
            commit('SET_IS_ADMIN', false)
          }
          commit('SET_INITIALIZED', true)
          resolve()
        })
      })
    },

    async login({ commit }, { email, password }) {
      commit('SET_LOADING', true)
      try {
        const userCredential = await signInWithEmailAndPassword(auth, email, password)
        const user = userCredential.user
        
        // Track successful sign in
        logAnalyticsEvent('login', {
          method: 'email_password'
        })
        
        commit('SET_USER', user)
        return user
      } catch (error) {
        console.error('Sign-in error:', error)
        
        // Track sign in error
        logAnalyticsEvent('login_error', {
          error_code: error.code,
          error_message: error.message
        })
        
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async logout({ commit }) {
      try {
        await signOut(auth)
        
        // Track successful sign out
        logAnalyticsEvent('logout', {
          method: 'explicit_signout'
        })
        
        commit('SET_USER', null)
      } catch (error) {
        console.error('Sign-out error:', error)
        
        // Track sign out error
        logAnalyticsEvent('logout_error', {
          error_message: error.message
        })
        
        commit('SET_ERROR', error.message)
        throw error
      }
    },

    async signInAnonymously({ commit }) {
      try {
        const userCredential = await signInAnonymously(auth)
        const user = userCredential.user
        
        // Track successful sign in
        logAnalyticsEvent('login', {
          method: 'anonymous'
        })
        
        commit('SET_USER', user)
        return user
      } catch (error) {
        console.error('Anonymous sign-in error:', error)
        
        // Track sign in error
        logAnalyticsEvent('login_error', {
          error_code: error.code,
          error_message: error.message
        })
        
        commit('SET_ERROR', error.message)
        throw error
      }
    },

    async signUp({ commit }, { email, password, name }) {
      try {
        const userCredential = await signInWithEmailAndPassword(auth, email, password)
        const user = userCredential.user
        
        // Update the user profile with the name
        await user.updateProfile({
          displayName: name
        })
        
        // Track successful sign up
        logAnalyticsEvent('sign_up', {
          method: 'email_password'
        })
        
        commit('SET_USER', user)
        return user
      } catch (error) {
        console.error('Sign-up error:', error)
        
        // Track sign up error
        logAnalyticsEvent('sign_up_error', {
          error_code: error.code,
          error_message: error.message
        })
        
        throw error
      }
    }
  },

  getters: {
    isAuthenticated: (state) => !!state.user,
    isAdmin: (state) => state.user?.email === 'thehollowaynorwich@gmail.com',
    isAnonymous: (state) => !!state.user?.isAnonymous
  }
} 